import { useNavigate } from "react-router-dom";

import './return-reason-list.scss';

import IconVisibility from '../../../../components/icons/icon-visibility/icon-visibility';
import IconEdit from '../../../../components/icons/icon-edit/icon-edit';
import Translation from '../../../../translation/translation';
import Icon from "../../../../components/icons/icon/icon";
import { ButtonIcon } from "../../../../components/inputs/button-icon/button-icon";
import GridLine from "../../../../components/layouts/grid-line-layout/grid-line/grid-line";
import GridLineButtons from "../../../../components/layouts/grid-line-layout/grid-line/grid-line-buttons/grid-line-buttons";
import GridLineIndicators from "../../../../components/layouts/grid-line-layout/grid-line/grid-line-indicators/grid-line-indicators";
import GridLineItem from "../../../../components/layouts/grid-line-layout/grid-line/grid-line-item/grid-line-item";
import GridLineLayout from "../../../../components/layouts/grid-line-layout/grid-line-layout";
import EditorPageLayout from "../../../../components/layouts/editor-page/editor-page-layout";
import { observer } from "mobx-react-lite";
import reasonListStore from "../../stores/reason-list-store";
import { useEffect, useState } from "react";
import usePage from "../../../../components/layouts/base-page-layout/page-hook";
import { FONT_COLOR, FONT_VARIANT } from "../../../../components/fonts/font-props";
import FontBody1 from "../../../../components/fonts/font-body1/font-body1";
import ReasonStore from "../../stores/reason-store";
import { RETURN_REASON_TYPE_MAP } from "../../constants";
import { Tooltip, TOOLTIP_VARIANTS } from "../../../../components/tooltip/tooltip";
import FontBody2 from "../../../../components/fonts/font-body2/font-body2";
import IconTranslationIndicator from "../../../../components/icons/icon-translation-indicator/icon-translation-indicator";
import useTranslation from "../../../../translation/hooks/translation-hook";
import { Button } from "../../../../components/inputs/button/button";
import { BUTTON_VARIANTS } from "../../../../components/inputs/button-variants";
import IconPlus from "../../../../components/icons/icon-plus/icon-plus";
import IconDelete from "../../../../components/icons/icon-delete/icon-delete";
import { ConfirmModal } from "../../../../components/modal/components/confirm-modal/confirm-modal";
import IconSort from "../../../../components/icons/icon-sort/icon-sort";
import { List } from "react-movable";
import { IPolicyRule } from "../../../../models/ReturnReasonTypes";
import { NotificationModal } from "../../../../components/modal/components/notification-modal/notification-modal";
import IconSwitchToExchange from "../../../../components/icons/icon-switch-to-exchange/icon-switch-to-exchange";


const ReturnReasonsList = observer(() => {
    const navigate = useNavigate();
    const { getTranslation, setTranslatableKeys, hasMissingTranslations } = useTranslation();
    const returnReasons = reasonListStore.returnReasons;
    const { setSubTitles } = usePage();
    const [ reasonToDelete, setReasonToDelete ] = useState(undefined as ReasonStore | undefined);
    const [ rulesUsingReason, setRulesUsingReason ] = useState(undefined as IPolicyRule[] | undefined);
    const { setDeletedKeys, save: saveTranslations } = useTranslation();

    useEffect(() => {
        setSubTitles(['Return Reasons']);
    }, [setSubTitles])

    useEffect(() => {
        const keys: string[] = [];
        returnReasons.forEach(reason => {
            keys.push(...reason.getTranslationKeys())
        });

        setTranslatableKeys(keys);

        return () => setTranslatableKeys([]);
    }, [returnReasons, setTranslatableKeys]);

    const getFuqText = (reason: ReasonStore): string => {
        if (reason.followUpQuestions.length === 1) {
            return `${reason.followUpQuestions.length} follow-up question`;
        }
        return `${reason.followUpQuestions.length} follow-up questions`;
    }

    const getCategory = (reason: ReasonStore): string => {
        return RETURN_REASON_TYPE_MAP[reason.type]?.label;
    }

    const onReasonClicked = (reason:ReasonStore) => navigate({
        pathname: reason.id.toString()
    });

    const getVisibilityIconTooltipVariant = (reason:ReasonStore) => {
        return reason.isEnabled ? TOOLTIP_VARIANTS.SUCCESS : TOOLTIP_VARIANTS.INFO;
    };

    const getVisibilityIconTooltipContent = (reason:ReasonStore) => {
        return reason.isEnabled
            ? <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>This reason will be <br /> presented to customers</FontBody2>
            : <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>This reason will NOT be <br /> presented to customers</FontBody2>;
    };

    const hasChanges = () => {
        return false;
    }

    const onReasonDelete = async (reason: ReasonStore) => {
        const policyRiles: IPolicyRule[] = await reason.delete();

        if (policyRiles.length === 0) {
            reason.setDeleted(setDeletedKeys);
            saveTranslations();
            reasonListStore.deleteReason(reason);
        } else {
            setRulesUsingReason(policyRiles);
        }
    }

    const reasonsList = returnReasons?.filter(r => !r.deleted).sort((a, b) => a.weight - b.weight) ?? [];

    const onSortEnd = (oldIndex: number, newIndex: number) => {
        /** if there was a try to reorder, but cancelled */
        if (oldIndex === newIndex) {
            return;
        }

        reasonsList?.forEach((reason, index) => {
            var down = oldIndex > newIndex;
            var changedIndex = down ? (index + 1) : (index - 1);
            var range = down ? (index <= oldIndex && index >= newIndex) : (index >= oldIndex && index <= newIndex);
            let newWeight = index;

            if (range) {
                newWeight = (index === oldIndex) ? newIndex : changedIndex;
            }
            reason.setWeight(newWeight);
        });

        reasonListStore.saveNewWeights();
    }

    return (
        <>
            <EditorPageLayout toolbar={{ hasLocalesSelector: true, hasBack: false, }}>
                <GridLineLayout>
                    <List
                        values={reasonsList}
                        onChange={({ oldIndex, newIndex }) => onSortEnd(oldIndex, newIndex)}
                        renderList={({ children, props }) => <div  {...props}>{children}</div>}
                        renderItem={({ value, props, isDragged }) =>
                            <div className="grid-line-holder" {...props}>
                                <GridLine key={value.id}
                                    onClick={() => onReasonClicked(value)}
                                    settings={{ columns: [{ width: 30, type: 'px' }, { width: 1.5 }, { width: 1 }, { width: 1 }, { width: 100, type: 'px' }, { width: 70, type: 'px' }] }}
                                    columns={[
                                        <GridLineItem>
                                            <div data-movable-handle="true" className="item-move">
                                                <Icon Component={IconSort} />
                                            </div>
                                        </GridLineItem>,

                                        <GridLineItem>
                                            {value.isSwitchToExchangeEnabled ? (
                                                <div className="switch-to-exchange">
                                                    <Translation Font={FontBody1} color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD} translationKey={value.displayName} />
                                                    <Tooltip content={<FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>Eligible for Switch to Exchange</FontBody2>}><Icon Component={IconSwitchToExchange} /></Tooltip>
                                                </div>
                                            ) : (
                                                <Translation Font={FontBody1} color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD} translationKey={value.displayName} />
                                            )}
                                        </GridLineItem>,

                                        <GridLineItem>
                                            <FontBody1 color={FONT_COLOR.PRIMARY3}>{getCategory(value)}</FontBody1>
                                        </GridLineItem>,

                                        <GridLineItem>
                                            <FontBody1 color={FONT_COLOR.PRIMARY3}>{getFuqText(value)}</FontBody1>
                                        </GridLineItem>,

                                        <GridLineIndicators>
                                            {hasMissingTranslations(undefined, value.getTranslationKeys())
                                                && <Tooltip
                                                    content={
                                                        <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                                                            There are untranslated items. Please edit <br />
                                                            the reason for more information.
                                                        </FontBody2>
                                                    }
                                                    variant={TOOLTIP_VARIANTS.ERROR}
                                                >
                                                    <Icon Component={IconTranslationIndicator} />
                                                </Tooltip>
                                            }
                                            <Tooltip
                                                variant={getVisibilityIconTooltipVariant(value)}
                                                content={getVisibilityIconTooltipContent(value)}
                                            >
                                                <Icon Component={IconVisibility} disabled={!value.isEnabled} />
                                            </Tooltip>
                                        </GridLineIndicators>,

                                        <GridLineButtons>
                                            <ButtonIcon Component={IconDelete} onClick={() => setReasonToDelete(value)} />
                                            <ButtonIcon Component={IconEdit} onClick={() => onReasonClicked(value)} />
                                        </GridLineButtons>,

                                    ]}
                                />
                            </div>
                        }
                    />
                </GridLineLayout>

                <div className="add-question-button">
                    <Button
                        variant={BUTTON_VARIANTS.Text}
                        text="add reason"
                        leftIcon={IconPlus}
                        disabled={hasChanges()}
                        onClick={() => {
                            const reasonId = reasonListStore.addReason();
                            navigate({
                                pathname: reasonId
                            });
                        }}
                    />
                </div>

            </EditorPageLayout>

            {reasonToDelete &&
                <ConfirmModal
                    title="Delete reason"
                    text={
                        <>
                            You asked to <strong>delete</strong> the
                            reason <strong>{getTranslation(reasonToDelete.displayName)}</strong>
                        </>
                    }
                    implicationText="Once deleted the reason and all its translations can’t be restored."
                    questionText="Are you sure you want to delete this reason?"
                    cancelButton={{
                        title: 'Cancel',
                        onClick: () => {
                            setReasonToDelete(undefined);
                        }
                    }}
                    okButton={{
                        title: 'Delete',
                        onClick: () => {
                            onReasonDelete(reasonToDelete);
                            setReasonToDelete(undefined);
                        }
                    }}
                />
            }

            {rulesUsingReason &&
                <NotificationModal
                    title="This reason can not be deleted as it is used in Policy Rules"
                    text={
                        <ul>
                            {rulesUsingReason.map(
                                (rule, index) =>
                                    <li key={index}>
                                        <FontBody2>{rule.name} (ID: {rule.id})</FontBody2>
                                    </li>
                                )
                            }
                        </ul>
                    }
                    okButton={{
                        title: 'OK',
                        onClick: () => setRulesUsingReason(undefined)
                    }}
                 />

            }
        </>
    )
});

export default ReturnReasonsList;
